import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { useSnackbar } from "notistack";
import { tracker } from "@utils/openreplay";

import { PrescriptionDetail } from "../../interfaces";
import {
  Body as BaseBody,
  Header,
  scrollToTop,
  NavigateBeforeButton,
  ContentContainer as BaseContentContainer,
  FormBody,
  FormContentContainer,
  FullScreenLoader,
} from "../../components";
import { SearchedMedicineCard } from "../MedicineSearch/components";
import { queryClient } from "../../context";
import { generatePrescriptionDetail } from "./utils/prescriptionDetails";
import { formatMedicineToSearch } from "./utils/formatMedicineToSearch";
import {
  useDeletePrescriptionDetail,
  useRequestData,
  useSearchProducts,
  useUpdatePrescriptionDetail,
} from "../../hooks";
import { MedicineTreatmentForm } from "./components/MedicineTreatmentForm";
import { usePrescriptionInteractions } from "@hooks/useInteractionAlerts";
import { useInteractionsStore } from "@hooks/useInteractionsStore";
import {
  MedicineDataContainer,
  MedicineTitle,
  MedicineDetail,
} from "./AddMedicineManually";

const MedicineCardContainer = styled(Box)(({ theme }) => ({
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  overflow: "hidden",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    maxWidth: 500,
  },
  alignSelf: "flex-start",
}));

const ContentContainer = styled(BaseContentContainer)(({ theme }) => ({
  alignItems: "flex-start",
  [theme.breakpoints.up("md")]: {
    gap: "16px",
  },
}));

const HeaderBody = styled(BaseBody)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",

  paddingBottom: 0,
  [theme.breakpoints.up("md")]: {
    padding: 24,
    paddingBottom: 0,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Body = styled(BaseBody)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    gap: "16px",
    padding: 24,
    paddingTop: 0,
    paddingBottom: 0,
    maxWidth: 1200,
  },
  flex: 1,
}));

const LoadingScreen = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
}));

const DeleteButton = styled(Box)(({ theme }) => ({
  marginLeft: "auto",
  color: theme.palette.primary.main,
  cursor: "pointer",
}));

export function PrescriptionTreatmentEdit() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { requestId = "", treatmentId } = useParams();

  const { data: requestData, isLoading: loadingRequestData } = useRequestData();

  const editedRequestDetail = useMemo(() => {
    if (!requestData || !requestData.details) return null;
    return (
      requestData.details.find((detail) => detail.id === treatmentId) || null
    );
  }, [requestData, treatmentId]);

  const { data, isPending, isSuccess } = useSearchProducts({
    query: editedRequestDetail?.medicine
      ? formatMedicineToSearch(editedRequestDetail?.medicine)
      : "",
    enabled: !!editedRequestDetail?.medicine,
  });

  const waitingForSearch = isPending && !!editedRequestDetail?.medicine;

  const updatedMedicineData = useMemo(() => {
    if (
      !isSuccess ||
      !data ||
      !editedRequestDetail ||
      !editedRequestDetail.medicine
    )
      return null;

    const medicineDetail = editedRequestDetail.medicine;
    const { activePrinciple, concentration, format, type } = medicineDetail;

    return (
      data.finalSearchResults.processedMedicines.find(
        (medicine) =>
          (medicine.type !== "GENERIC" &&
            type !== "GENERIC" &&
            "id" in medicineDetail &&
            medicine.id === medicineDetail.id) ||
          (medicine.type === "GENERIC" &&
            type === "GENERIC" &&
            medicine.activePrinciple === activePrinciple &&
            medicine.concentration === concentration &&
            medicine.format === format)
      ) || null
    );
  }, [data, editedRequestDetail, isSuccess]);

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const { mutateAsync: updateDetail, isPending: isUpdatingDetail } =
    useUpdatePrescriptionDetail();

  const { mutate: deleteDetail, isPending: isDeletingDetail } =
    useDeletePrescriptionDetail({
      requestId,
      request: requestData?.request,
      onSuccess: () => {
        navigate(`/app/solicitudes/${requestId}/receta/crear`);
      },
    });

  const { deleteDrug } = useInteractionsStore(requestId);

  const { handleRefetch } = usePrescriptionInteractions(
    editedRequestDetail?.activePrinciple ?? ""
  );

  const onSubmit = useCallback(
    async (formData: PrescriptionDetail) => {
      try {
        const detailId = treatmentId ?? editedRequestDetail?.id;
        if (!detailId) throw new Error("missing detail id");

        scrollToTop();

        await updateDetail({
          requestId,
          detailId,
          prescriptionDetail: editedRequestDetail?.medicine
            ? generatePrescriptionDetail(
                editedRequestDetail?.medicine,
                formData
              )
            : formData,
        });
        await handleRefetch();
        await queryClient.invalidateQueries({
          queryKey: ["request", requestId],
        });
        tracker?.event("UpdatePrescriptionDetailSuccess", {
          source: "SubmitPrescriptionTreatmentEdit",
          requestId,
          treatmentId,
        });

        navigate(`/app/solicitudes/${requestId}/receta/crear`, {
          state: {
            detailAdded: true,
            medicineType:
              editedRequestDetail?.prescriptionType === "HELD"
                ? "HELD"
                : "SIMPLE",
          },
        });
      } catch (error) {
        console.error("Error updating prescription detail: ", error);
        tracker?.event("UpdatePrescriptionDetailError", {
          source: "SubmitPrescriptionTreatmentEdit",
          error,
          requestId,
          treatmentId,
        });
        enqueueSnackbar("Error al editar tratamiento", {
          variant: "error",
        });
      }
    },
    [
      editedRequestDetail?.medicine,
      editedRequestDetail?.id,
      editedRequestDetail?.prescriptionType,
      treatmentId,
      updateDetail,
      requestId,
      handleRefetch,
      navigate,
      enqueueSnackbar,
    ]
  );

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDelete = useCallback(() => {
    if (!editedRequestDetail) return;
    deleteDetail(editedRequestDetail);
    if (requestData) {
      deleteDrug(
        editedRequestDetail.activePrinciple ?? "",
        requestData.details ?? []
      );
    }
  }, [deleteDetail, deleteDrug, editedRequestDetail, requestData]);

  if (!editedRequestDetail) {
    navigate(`/app/solicitudes/${requestId}/receta/crear`);
    return null;
  }

  if (waitingForSearch || loadingRequestData) {
    return (
      <LoadingScreen>
        <FullScreenLoader
          title={
            loadingRequestData
              ? "Cargando información del tratamiento..."
              : "Cargando medicamento..."
          }
        />
      </LoadingScreen>
    );
  }

  const form = (
    <MedicineTreatmentForm
      prefilledMedicineAttributes={editedRequestDetail}
      formManagement={{
        onSubmit,
        isLoading: isUpdatingDetail,
        handleDelete,
        isDeleting: isDeletingDetail,
      }}
      isEditing
    />
  );

  const treatmentForm = editedRequestDetail.medicine ? (
    form
  ) : (
    <FormContentContainer disableGutters>
      <FormBody sx={{ alignItems: "flex-start" }}>
        <MedicineDataContainer>
          <MedicineTitle>{editedRequestDetail.activePrinciple}</MedicineTitle>
          {editedRequestDetail.name && (
            <MedicineDetail>{editedRequestDetail.name}</MedicineDetail>
          )}
        </MedicineDataContainer>
        {form}
      </FormBody>
    </FormContentContainer>
  );

  return (
    <>
      <Header
        title={!isDesktop ? "Editar Tratamiento" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />

      <ContentContainer disableGutters>
        <HeaderBody>
          <Typography variant="h4" fontWeight={"medium"}>
            Editar Tratamiento
          </Typography>
          <DeleteButton
            onClick={handleDelete}
            style={{ opacity: isDeletingDetail ? 0.6 : 1 }}
          >
            <Typography variant="body1" fontWeight={500}>
              {isDeletingDetail ? "Eliminando..." : "Eliminar"}
            </Typography>
          </DeleteButton>
        </HeaderBody>
        <Body>
          {!waitingForSearch && editedRequestDetail.medicine && (
            <MedicineCardContainer>
              <SearchedMedicineCard
                medicine={updatedMedicineData ?? editedRequestDetail.medicine}
                isTreatmentCard={true}
              />
            </MedicineCardContainer>
          )}
          {treatmentForm}
        </Body>
      </ContentContainer>
    </>
  );
}
